/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    hasValidRecAccount,
    isRegisteredUserLoggedIn,
    isGuestLoggedIn,
} from 'sarsaparilla/auth';

import {
    openLoginModal,
    openLoginWithGuestModal,
    openForgotPasswordModal,
    closeAuthModal,
    type AuthModalState,
} from '../redux/authModal.slice';

/** Options for {@link AuthModalContextValue.ensureLoggedIn}. */
export interface EnsureLoggedInOptions {
    /**
     * When `true`, open the login modal that also offers "Continue as a Guest".
     * A successful guest login resolves the promise with `true`. Defaults to
     * `false` (standard login modal, no guest option).
     */
    allowGuest?: boolean;
}

export interface AuthModalContextValue {
    openLoginModal: () => void;
    openLoginWithGuestModal: () => void;
    openForgotPasswordModal: () => void;
    closeAuthModal: () => void;
    /**
     * Ensure the user is logged in before continuing an action.
     *
     * - If already logged in, resolves `true` immediately without opening a modal.
     * - Otherwise opens the login modal and resolves once the flow finishes:
     *   `true` when the user logs in (password, MFA, create-account, or guest),
     *   `false` when the user closes/cancels the modal.
     *
     * Concurrent calls while a request is in flight share the same promise.
     *
     * @example
     * const { ensureLoggedIn } = useAuthModal();
     * if (await ensureLoggedIn()) {
     *     addToCart(item);
     * }
     */
    ensureLoggedIn: (options?: EnsureLoggedInOptions) => Promise<boolean>;
}

const AuthModalContext = React.createContext<AuthModalContextValue | undefined>(
    undefined
);

/**
 * Minimal slice of redux state this provider observes to detect login
 * completion. Redux is kept as an internal implementation detail; consumers
 * only interact through {@link useAuthModal}.
 */
interface AuthModalProviderReduxState {
    auth?: { account?: unknown };
    authModal?: AuthModalState;
}

interface PendingLoginRequest {
    promise: Promise<boolean>;
    resolve: (loggedIn: boolean) => void;
}

export function useAuthModal() {
    const context = React.useContext(AuthModalContext);

    if (!context) {
        throw new Error('useAuthModal must be used within an AuthModalProvider');
    }
    return context;
}

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch();

    const isLoggedIn = useSelector((state: AuthModalProviderReduxState) =>
        Boolean(state.auth?.account)
    );
    const screen = useSelector(
        (state: AuthModalProviderReduxState) => state.authModal?.screen ?? null
    );

    // A single, app-wide pending login request. Held in a ref so that opening
    // the modal (which only mutates the ref) does not cause a re-render, and so
    // concurrent callers can share the same in-flight promise.
    const pendingRef = React.useRef<PendingLoginRequest | null>(null);

    // Resolve the pending request when the relevant auth state transitions.
    React.useEffect(() => {
        const pending = pendingRef.current;
        if (!pending) {
            return;
        }

        if (isLoggedIn) {
            // Success — covers password, MFA, create-account, and guest flows,
            // since all of them ultimately populate the logged-in account.
            pendingRef.current = null;
            pending.resolve(true);
        } else if (!screen) {
            // The modal closed while still logged out — the user cancelled.
            pendingRef.current = null;
            pending.resolve(false);
        }
        // Any non-null screen (login, mfaCode, createAccount, requestPasswordReset)
        // means the user is still mid-flow, so keep waiting.
    }, [isLoggedIn, screen]);

    // If AuthModalProvider unmounts while a login request is in flight, resolve
    // false so consumers aren't left awaiting an unresolved promise.
    // This is essentially a no-op because it would mean the entire app is being torn down,
    // but it's a good defensive programming practice
    React.useEffect(() => {
        return () => {
            if (pendingRef.current) {
                pendingRef.current.resolve(false);
                pendingRef.current = null;
            }
        };
    }, []);

    const ensureLoggedIn = React.useCallback(
        (options?: EnsureLoggedInOptions): Promise<boolean> => {
            // Already logged in: resolve immediately using the same predicate the
            // modal slice gates on, so we never open a no-op modal.
            if (isRegisteredUserLoggedIn()) {
                return Promise.resolve(true);
            }

            if (options?.allowGuest && hasValidRecAccount()) {
                return Promise.resolve(true);
            }

            // Guest signed in but a registered user is required: resolve false
            // without opening the modal. The slice won't open one for a guest
            // (hasValidRecAccount is true), so waiting would never settle, and
            // there is no way to promote a guest to a registered account.
            // Callers can't tell this apart from a cancel. That's accepted for
            // now: guests are only allowed on purchase-flow routes and are logged
            // out when they leave them, so in practice they don't reach
            // registered-only actions. Revisit if guests can go further.
            if (isGuestLoggedIn() && !options?.allowGuest) {
                return Promise.resolve(false);
            }

            // A request is already in flight: share its promise. The shared
            // promise ignores this call's `allowGuest`, so a registered-only
            // caller can get `true` if the in-flight guest-enabled modal ends
            // in a guest login. Accepted for the same reason as above: guest
            // flows don't overlap with registered-only actions.
            if (pendingRef.current) {
                return pendingRef.current.promise;
            }

            let resolveFn: (loggedIn: boolean) => void = () => {};
            const promise = new Promise<boolean>((resolve) => {
                resolveFn = resolve;
            });

            pendingRef.current = { promise, resolve: resolveFn };

            if (options?.allowGuest) {
                dispatch(openLoginWithGuestModal());
            } else {
                dispatch(openLoginModal());
            }

            return promise;
        },
        [dispatch]
    );

    const value = React.useMemo(() => {
        return {
            openLoginModal: () => {
                dispatch(openLoginModal());
            },
            openLoginWithGuestModal: () => {
                dispatch(openLoginWithGuestModal());
            },
            openForgotPasswordModal: () => {
                dispatch(openForgotPasswordModal());
            },
            closeAuthModal: () => {
                dispatch(closeAuthModal());
            },
            ensureLoggedIn,
        };
    }, [dispatch, ensureLoggedIn]);

    return (
        <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>
    );
}
